import { Card, Typography, Box } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList
} from "recharts";

/* ===== Fixed label component (KEY PART) ===== */
const FixedTopLabel = ({ x, y, width, value }) => {
  if (value == null) return null;

  return (
    <text
      x={x + width / 2}
      y={y - 6}                // keeps all labels aligned
      textAnchor="middle"
      fontSize={8}
      fill="#11111180"
      fontWeight={400}
    >
      {value}%
    </text>
  );
};

export default function MultiBarcharts({
  title,
  xLabels,
  series,
  dataMatrix,
  yTicks
}) {
  /* ===== Convert matrix to recharts format ===== */
  const chartData = xLabels.map((label, i) => {
    const row = { month: label };
    series.forEach((s, j) => {
      row[s.label] = dataMatrix[i][j];
    });
    return row;
  });
  const chartWidth = Math.max(xLabels.length * 90, 900);

  return (
    <Card
      sx={{
        p: 2,
        borderRadius: "12px !important",
        background: "#fff",
         boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.1)",
        position: "relative",
        overflow: "visible"
      }}
    >
      {/* ===== Title Bar ===== */}
      <Box
        sx={{
          position: "absolute",
          top: "-10px",
          left: "12px",
          right: "12px",
          height: "36px",
          background: "#115f9c",
          borderRadius: "4px !important",
          display: "flex",
          alignItems: "center",
          px: "12px",
          color: "#fff",
          fontSize: { xs: 12, sm: 12, md: 16 },
          fontWeight: 200,
          boxShadow:"0px 1px 16px rgba(128, 203, 255, 0.5)"
        }}
      >
        {title}
      </Box>

      {/* ===== Chart ===== */}
      <Box sx={{
        width: "100%",
        height: 420,
        overflowX: "auto",
        overflowY: "hidden"
      }}>
        <Box
          sx={{
            width: {
              xs: chartWidth,   //  enable horizontal scroll on mobile
              sm: chartWidth,   // enable horizontal scroll on tablet
              md: chartWidth,
              lg: "100%"
            },
            minWidth: {
              xs: chartWidth,
              sm: chartWidth,
              md: chartWidth,
              lg: "100%"
            },
            height: "100%",
          }}
        >
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              barSize={20}
              barGap={6}
              barCategoryGap={22}
              margin={{ top: 40, right: 30, left: 10, bottom: 10 }}
            >
              <CartesianGrid stroke="#1111110D" vertical={false} />

              <XAxis
                dataKey="month"
                tick={{ fontSize: 10, fill: "#111111" }}
                axisLine={false}
                tickLine={false}
                interval={0}
              />

              <YAxis
                domain={[75, 100]}
                ticks={yTicks}
                tick={{ fontSize: 10, fill: "#11111180", dx: -12, fontWeight: 200 }} // move labels left
                axisLine={false}
                tickLine={false}
                label={{
                  value: "Percentage (%)",
                  angle: -90,
                  position: "insideLeft",
                  dx: 5,
                  dy: 40,
                 style: { fontSize: 10, fill: "#111111", fontWeight: 200 }
                }}
              />

              <Tooltip formatter={(v) => `${v}%`} />

              {/* ===== GROUPED BARS ===== */}
              {series.map((s) => (
                <Bar
                  key={s.label}
                  dataKey={s.label}
                  fill={s.color}
                  radius={[0, 0, 0, 0]}
                >
                  <LabelList
                    dataKey={s.label}
                    content={<FixedTopLabel />}
                  />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>

        </Box>

      </Box>

      {/* ===== Legend ===== */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 2,
          mt: 3
        }}
      >
        {series.map((s) => (
          <Box
            key={s.label}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: s.color
              }}
            />
            <Typography fontSize={10} style={{ color: "#111111",fontWeight:200 }}>{s.label}</Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
}
